import React, { useContext, useEffect, useRef, useState } from "react";

import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormGroup from "@material-ui/core/FormGroup";
import FormLabel from "@material-ui/core/FormLabel";
import IconButton from "@material-ui/core/IconButton";
import MenuItem from "@material-ui/core/MenuItem";
import Popover from "@material-ui/core/Popover";
import Tooltip from "@material-ui/core/Tooltip";
import { createStyles, Theme, withStyles, WithStyles } from "@material-ui/core/styles";

import HelpTip, { HelpTipContainer } from "app/HelpTip";
import Icon from "app/Icon";
import SearchField from "app/SearchField";
import request from "app/http";
import _ from "app/lang";
import { SiteAttributes } from "app/models/Site";
import User from "app/models/User";
import { pageContext } from "app/page/ContextProvider";
import Link from "app/page/Link";
import { createEvent } from "app/utils";

const SHOW_FILTER_THRESHOLD = 10;
const FETCH_LIMIT = 100;
const FILTER_XHR_DELAY = 600;

const styles = (theme: Theme) => createStyles({
    button: {
        width: 180,
        [theme.breakpoints.down("sm")]: {
            width: 210
        },
        borderStyle: "solid",
        borderWidth: 1,
        borderColor: "rgba(0, 0, 0, 0.23)",
        borderTopColor: "rgba(0, 0, 0, 0.23)",
        borderRightColor: "rgba(0, 0, 0, 0.23)",
        borderBottomColor: "rgba(0, 0, 0, 0.23)",
        borderLeftColor: "rgba(0, 0, 0, 0.23)",
        appearance: "none",
        background: "transparent",
        textAlign: "left",
        color: "inherit",
        display: "flex",
        padding: "0 12px 0 16px",
        lineHeight: "30px",
        cursor: "pointer",
        textTransform: "none",
        borderRadius: 4,
        marginBottom: theme.spacing(1),
        fontWeight: "normal",

        "&:disabled": {
            color: "inherit",

            "& $icon": {
                display: "none",
            }
        },
    },

    text: {
        flex: "1 1 auto",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis"
    },

    icon: {
        flexShrink: 0
    },

    search: {
        padding: theme.spacing(1)
    },

    truncated: {
        display: "inline-block",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap"
    },

    paper: {
        display: "flex",
        flexDirection: "column",
        width: theme.spacing(30),
        [theme.breakpoints.down("xs")]: {
            width: "100%"
        }
    },

    header: {
        flexShrink: 0,
        flexGrow: 0
    },

    footer: {
        flexShrink: 0,
        flexGrow: 0
    },

    body: {
        flexGrow: 1,
        flexShrink: 1,
        overflow: "auto",
        "-ms-overflow-style": "-ms-autohiding-scrollbar",
        // maxHeight: theme.spacing(60)
    },

    actions: {
        display: "flex",
        justifyContent: "flex-end",
        padding: theme.spacing(1)
    },

    labels: (props: Props) => ({
        width: "100%",
        marginLeft: 0,
        cursor: props.disabled ? "revert" : undefined,
    }),

    labelTypography: {
        marginBottom: 6,
    },

    helperTextLabel: {
        display: "block"
    }
});

export type SiteSelectorClasses = Partial<WithStyles<typeof styles>["classes"]>;

export interface SiteInfo {
    id: string;
    name: string;
}

export type SiteSelectorSite = SiteInfo | SiteAttributes;

interface Props {
    fetchOnLoad?: boolean;
    fetchAllFields?: boolean;
    placeholder?: string;
    disabled?: boolean;
    label?: string;
    showLabel?: boolean;
    withCreatingSites?: boolean;
    selected: SiteSelectorSite | null;
    excludeSiteId?: string;
    includeSiteId?: string;
    onFetchSites?: (sites: SiteSelectorSite[]) => void;
    onChange: (site: SiteSelectorSite) => void;
    error?: boolean;
    helperText?: string;
}

let fetchTimer: number;

function SiteSelector(props: Props & WithStyles<typeof styles>) {
    const context = useContext(pageContext);
    const user = new User(context);

    const {
        classes,
        placeholder = _("Choose a Site..."),
        disabled = false,
        fetchOnLoad = false,
        fetchAllFields = false,
        withCreatingSites = false,
        label = _("Site"),
        showLabel = false
    } = props;

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [selected, setSelected] = useState<SiteSelectorSite | null>(props.selected);
    const [filter, setFilter] = useState<string | undefined>(undefined);
    const [sites, setSites] = useState<SiteSelectorSite[] | undefined>(undefined);
    const [backendFilter, setBackendFilter] = useState<boolean>(false);

    const anchorElement = useRef<HTMLButtonElement>(null);

    const buttonText = (selected) ? selected.name : placeholder;
    const buttonIcon = isOpen ? "arrow_drop_up" : "arrow_drop_down";

    useEffect(() => {
        if (sites && sites.length > 0) {
            // hack to reload the popover or else it site list may be positioned out of screen
            window.dispatchEvent(createEvent("resize"));
        }
    }, [sites && sites.length > 0]);

    useEffect(() => {
        if (fetchOnLoad) {
            fetchSites(undefined);
        }
    }, []);

    useEffect(() => {
        if (sites) {
            const siteToSelect = sites.find(s => s.id === props.selected?.id);

            setSelected(siteToSelect ?? null);
        }
    }, [props.selected?.id, sites?.map(s => s.id).join(",")]);

    const fetchSites = (_filter: string | undefined) => {
        request({
            url: "/apiv2/cloud/:cloudId/sites-select",
            data: {
                limit: FETCH_LIMIT,
                filter: _filter,
                allFields: fetchAllFields,
                includeIds: props.includeSiteId,
            }
        }).then((data: SiteSelectorSite[]) => {
            if (data.length === FETCH_LIMIT) {
                setBackendFilter(true);
            }

            let filteredSites = data;

            if (props.excludeSiteId) {
                filteredSites = data.filter((_site: SiteSelectorSite) => _site.id !== props.excludeSiteId);
            }

            setSites(filteredSites);

            if (props.onFetchSites) {
                props.onFetchSites(filteredSites);
            }
        });
    };

    const toggleMenu = () => {
        if (!isOpen) {
            fetchSites(filter);
        }

        setIsOpen(!isOpen);
    };

    const searchRef = useRef<HTMLDivElement>(null);

    let searchField = null;

    if (sites && sites.length >= SHOW_FILTER_THRESHOLD || backendFilter) {
        const handleFilterChange = (value: string) => {
            const _filter = value.toLowerCase();

            setFilter(_filter);
            window.clearTimeout(fetchTimer);

            if (backendFilter) {
                setSites(undefined);
                fetchTimer = window.setTimeout(() => fetchSites(_filter), FILTER_XHR_DELAY);
            }
        };

        searchField = (
            <div className={classes.header} tabIndex={-1} ref={searchRef}>
                <SearchField
                    className={classes.search}
                    onChange={handleFilterChange}
                    placeholder={_("Filter")}
                    initialValue={filter}
                />
                <Divider />
            </div>
        );
    }

    let visibleSites: SiteSelectorSite[] = [];

    if (sites) {
        visibleSites = sites;
    }

    if (filter && filter.length > 0) {
        visibleSites = visibleSites.filter(site => site.name.toLowerCase().includes(filter));
    }

    const handleCreateSiteClick = () => setIsOpen(false);

    let notice = null;

    if (!sites) {
        notice = (
            <MenuItem disabled>{_("Searching")}</MenuItem>
        );
    } else if (visibleSites.length === 0) {
        if (filter) {
            notice = (
                <MenuItem disabled>{_("No matching sites found.")}</MenuItem>
            );
        } else {
            notice = (
                <Link to="/v2/cloud/:cloudId/sites/add" onClick={handleCreateSiteClick}>
                    <MenuItem >
                        <HelpTipContainer>
                            {_("Please create a site.")}
                            <HelpTip
                                title={_(
                                    "A “site” is a logical grouping of devices that may share " +
                                    "common configuration settings, a common location, " +
                                    "and/or common wireless clients and stats."
                                )}
                                variant="default"
                                filled
                            />
                        </HelpTipContainer>
                    </MenuItem>
                </Link>
            );
        }
    }

    const handleClick = (site: SiteSelectorSite) => {
        setSelected(site);
        setIsOpen(false);
        props.onChange(site);
    };

    const handleEntered = () => {
        if (searchRef.current !== null) {
            const input = searchRef.current.querySelector("input");

            if (input !== null) {
                input.focus();
            }
        }
    };

    let createNewSiteFooter;

    if (withCreatingSites && user.hasPermission("manage_sites")) {
        createNewSiteFooter = (
            <div className={classes.footer}>
                <Divider />
                <div className={classes.actions}>
                    <Link to="/v2/cloud/:cloudId/sites">
                        <Tooltip title={_("Manage Sites")}>
                            <IconButton size="small">
                                <Icon id="settings" small />
                            </IconButton>
                        </Tooltip>
                    </Link>
                    <Link to="/v2/cloud/:cloudId/sites/add" onClick={handleCreateSiteClick}>
                        <Tooltip title={_("Create a new site")}>
                            <IconButton size="small">
                                <Icon id="add" small />
                            </IconButton>
                        </Tooltip>
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <>
            <FormGroup row>
                <FormControlLabel
                    labelPlacement="start"
                    label={showLabel ? label : null}
                    classes={{
                        root: classes.labels,
                        label: classes.labelTypography
                    }}
                    control={(
                        <Box>
                            <Button
                                variant="text"
                                disabled={disabled}
                                className={classes.button}
                                onClick={toggleMenu}
                                disableRipple={true}
                                ref={anchorElement}
                            >
                                <div className={classes.text}>{buttonText}</div>
                                <Icon id={buttonIcon} className={classes.icon} />
                            </Button>
                            {props.error && (
                                <FormLabel component="span" error className={classes.helperTextLabel}>
                                    {props.helperText}
                                </FormLabel>
                            )}
                        </Box>
                    )}
                />
                <Popover
                    anchorEl={anchorElement.current}
                    open={isOpen}
                    anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
                    onEntered={handleEntered}
                    onExited={() => null}
                    onClose={toggleMenu}
                    classes={{ paper: classes.paper }}
                >
                    {searchField}
                    {notice}
                    <div className={classes.body}>
                        {visibleSites.map(site => (
                            <MenuItem key={site.id}
                                onClick={() => handleClick(site)}
                                selected={selected !== null && site.id === selected.id}
                            >
                                <span className={classes.truncated}>{site.name}</span>
                            </MenuItem>
                        ))}
                    </div>
                    {createNewSiteFooter}
                </Popover>
            </FormGroup>
        </>
    );
}

export default withStyles(styles)(SiteSelector);
